/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: Mappings view inside the lg.Logic OBC — shows the mapping table behind the typed FK with its entries, partner overrides, typed dependants and the shared quick test.
 */
import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {LogicDto} from '../../../model/dto/entity/logic.dto';
import {LogicMappingTableDto} from '../../../model/dto/entity/logic-mapping-table.dto';
import {LogicMappingTableService} from '../../../service/api/logic-mapping-table.service';
import {LogicDependencyResultDto, LG_MAPPING_TABLE_DEPENDENCY_COVERAGE_NOTE} from '../../../model/dto/api/logic-dependency-result.dto';
import {LogicDependentReferenceDto} from '../../../model/dto/api/logic-dependent-reference.dto';
import {getMappingFallbackLabel, MappingFallbackEnum} from '../../../enum/mapping-fallback.enum';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../../core/shared/misc/sorting';
import {MvsObjectNavigationService} from '../../../../../core/shared/navigation/mvs-object-navigation.service';
import {ObjectIdentifier} from '../../../../../core/shared/basic/object-identifier';
import {DtoDetail} from '../../../../../core/shared/dto/dto.detail';
import {WidgetToolbarCallbackInterface} from '../../../../../core/shared/widget/widget-toolbar-callback.interface';
import {MvsFormControlOverwrite} from '../../../../../core/shared/logic/form/logic/mvs-form-control-overwrite';
import {MvsFormFieldAccessEnum} from '../../../../../core/shared/logic/form/logic/mvs-form-field-access.enum';

/**
 * The mappings surface of a logic written in `LogicLanguage.mapping`.
 *
 * It renders the one table the logic points at: its fallback configuration, its entries, its partner
 * overrides, everything that references it through a typed foreign key, and the shared quick test.
 *
 * The dependants list carries a coverage note that must never be dropped. The backend query walks
 * typed foreign keys only; script calls (`$ctx.lookupMapping`) and direct Java calls name the table by
 * an alias string and are invisible to it — one of them from compiled code. An empty list therefore
 * means 'no typed reference', not 'nothing uses this table', and a surface that lets it read as the
 * latter turns an incomplete search into a false all-clear.
 */
@Component({
    selector: 'mvs-lg-logic-mappings',
    templateUrl: './lg-logic-mappings.component.html',
    styleUrls: ['./lg-logic-mappings.component.scss'],
    standalone: false
})
export class LgLogicMappingsComponent implements OnInit, OnChanges {

    /** The mapping logic whose table is shown. */
    @Input() logicDto: LogicDto;

    initialized: boolean = false;
    busy: boolean = false;
    dependentsBusy: boolean = false;

    mappingTable: LogicMappingTableDto | null = null;
    loadErrorMessage: string | null = null;

    dependents: LogicDependentReferenceDto[] = [];
    dependentsErrorMessage: string | null = null;

    widgetEntries: WidgetData;
    widgetOverrides: WidgetData;

    /** The coverage limit of the dependants list, rendered next to it — see the class comment. */
    readonly dependencyCoverageNote: string = LG_MAPPING_TABLE_DEPENDENCY_COVERAGE_NOTE;

    constructor(
        protected mappingTableService: LogicMappingTableService,
        protected navigationService: MvsObjectNavigationService) {
    }

    ngOnInit(): void {
        this.refreshComponent();
        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }

        if (changes['logicDto']) {
            this.refreshComponent();
        }
    }

    /** The mapping table id behind the typed FK; absent when the logic is not configured yet. */
    getMappingTableId(): number | null {
        return this.logicDto?.mappingTableDtoId ?? null;
    }

    /** True when the logic has no table yet — a configuration gap, not an error. */
    isUnconfigured(): boolean {
        return !this.getMappingTableId();
    }

    refreshComponent(): void {
        this.mappingTable = null;
        this.dependents = [];
        this.loadErrorMessage = null;
        this.dependentsErrorMessage = null;
        this.widgetEntries = null;
        this.widgetOverrides = null;

        const mappingTableId: number = this.getMappingTableId();
        if (!mappingTableId) {
            return;
        }

        this.loadMappingTable(mappingTableId);
        this.loadDependents(mappingTableId);
        this.refreshWidgets(mappingTableId);
    }

    loadMappingTable(mappingTableId: number): void {
        this.busy = true;

        this.mappingTableService.get(mappingTableId).subscribe({
            next: (res) => {
                this.mappingTable = res as LogicMappingTableDto;
                this.busy = false;
            },
            error: () => {
                this.loadErrorMessage = 'Die Mapping-Tabelle konnte nicht geladen werden.';
                this.busy = false;
            }
        });
    }

    loadDependents(mappingTableId: number): void {
        this.dependentsBusy = true;

        this.mappingTableService.getDependents(mappingTableId).subscribe({
            next: (res: LogicDependencyResultDto) => {
                this.dependents = res?.dependents ?? [];
                this.dependentsBusy = false;
            },
            error: () => {
                this.dependents = [];
                this.dependentsErrorMessage = 'Die Abhängigkeiten konnten nicht geladen werden.';
                this.dependentsBusy = false;
            }
        });
    }

    /**
     * Builds the two child widgets.
     *
     * The filters use the relation name with the `Id` suffix and `FilterCriteria.cOperatorEqual` —
     * a foreign-key filter that names the plain column, or an operator passed under the wrong property
     * name, returns zero rows without any error.
     *
     * The overrides are sorted by `sourceValue`, NOT by the partner's name. A relation registers in the
     * QL meta model only under `<relation>Id`; there is no `partnerDtoName` and no `partnerName`
     * member, and the resolver's `Id`-suffix fallback does not apply to a name that does not end in
     * `Id` — the sort would end in `QlRuntimeException("Attribute e.partnerDtoName not found!")`
     * (`QlDpEntityJdbcConditionsResolver:396-417`). `sourceValue` is a real column and is also the
     * order a lookup table is read in.
     */
    refreshWidgets(mappingTableId: number): void {
        this.widgetEntries = WidgetFactory.createWidgetTableEntityQl(
            'lg.logic.mappings.entries',
            'Einträge',
            'lg.LogicMappingTableEntry',
            'Diese Mapping-Tabelle hat noch keine Einträge. Legen Sie den ersten Eintrag an, damit die Logik einen Wert auflösen kann.',
            [FilterCriteria.create('e.mappingTableId', FilterCriteria.cOperatorEqual, mappingTableId)],
            [new Sorting('e.orderPriority', true)]
        );

        this.widgetOverrides = WidgetFactory.createWidgetTableEntityQl(
            'lg.logic.mappings.overrides',
            'Partner-Overrides',
            'lg.LogicMappingTableOverride',
            'Keine Partner-Overrides vorhanden — die Tabelle löst für alle Partner gleich auf.',
            [FilterCriteria.create('e.mappingTableId', FilterCriteria.cOperatorEqual, mappingTableId)],
            [new Sorting('e.sourceValue', true)],
            // resolveFkeyTexts — the partner column is a foreign key and would otherwise render as a
            // bare id here while the same widget on the mapping-table OBC resolves it to a name.
            true
        );

        this.widgetEntries.functionCallbacks = this.widgetToolbarCreate(mappingTableId);
        this.widgetOverrides.functionCallbacks = this.widgetToolbarCreate(mappingTableId);
    }

    /**
     * Pre-fills the mapping-table FK on a new child row and locks the field.
     *
     * Without it, the widget's 'new' button produces an entry with no table behind it — a row that is
     * saved, invisible in every filtered list, and therefore very hard to find again.
     */
    protected widgetToolbarCreate(mappingTableId: number) {
        return {
            onFunctionCreateNew: (): WidgetToolbarCallbackInterface => {
                const dto: DtoDetail = new DtoDetail();
                dto['mappingTableDtoId'] = mappingTableId;

                const formControlOverwrite: MvsFormControlOverwrite = new MvsFormControlOverwrite();
                formControlOverwrite.addField('mappingTableDtoId', MvsFormFieldAccessEnum.READ);

                return {
                    dto: dto,
                    formControlOverwrite: formControlOverwrite
                };
            }
        };
    }

    /** Count of typed dependants, with the right German singular/plural. */
    getDependentsCountText(): string {
        const count: number = this.dependents?.length ?? 0;
        return `${count} ${count === 1 ? 'Referenz' : 'Referenzen'}`;
    }

    /** German label of the fallback strategy, including the 'unset behaves like ERROR' case. */
    getFallbackLabel(): string {
        return getMappingFallbackLabel(this.mappingTable?.fallbackBehaviour);
    }

    /** True when the fallback value itself is what gets emitted on a miss. */
    showsFallbackValue(): boolean {
        return this.mappingTable?.fallbackBehaviour === MappingFallbackEnum.DEFAULT_VALUE;
    }

    /** German label for one dependant's usage type — the field is `usageType`, not `referenceType`. */
    getUsageTypeLabel(dependent: LogicDependentReferenceDto): string {
        switch (dependent?.usageType) {
            case 'MAPPING_LOGIC':
                return 'Mapping-Logik';
            case 'MAPPING_LOOKUP':
                return 'Flow-Schritt (Mapping-Lookup)';
            default:
                return dependent?.usageType ?? dependent?.entityType ?? 'Referenz';
        }
    }

    /**
     * Opens the mapping table in its own tab, so it is maintained in exactly one place instead of
     * being duplicated into this panel.
     *
     * Navigates by object identity rather than by a hard-coded path: the service resolves the entity's
     * registered route and falls back to the generic object route, so the link works whether or not a
     * dedicated object page has been registered for `lg.LogicMappingTable`.
     */
    openMappingTable(event?: MouseEvent): void {
        const mappingTableId: number = this.getMappingTableId();

        if (!mappingTableId) {
            return;
        }

        this.navigationService.handleObjectNavigation(
            new ObjectIdentifier('lg.LogicMappingTable', mappingTableId),
            event,
            {openNewTab: true}
        );
    }
}
